import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { getStatusColor, getStatusText, getErrorIcon, formatQuotaErrorForDisplay } from '../utils/campaignReportHelpers';

const REPORT_ITEMS_PER_PAGE = 15;

type ErrorTooltipState = {
  x: number;
  y: number;
  formatted: string;
  category: string;
  icon: string;
  bgColor: string;
  color: string;
} | null;

export function CampaignReportPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportPage, setReportPage] = useState(1);
  const [errorTooltip, setErrorTooltip] = useState<ErrorTooltipState>(null);
  const errorTooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleHideErrorTooltip = () => {
    if (errorTooltipHideRef.current) clearTimeout(errorTooltipHideRef.current);
    errorTooltipHideRef.current = setTimeout(() => setErrorTooltip(null), 120);
  };
  const cancelHideErrorTooltip = () => {
    if (errorTooltipHideRef.current) {
      clearTimeout(errorTooltipHideRef.current);
      errorTooltipHideRef.current = null;
    }
  };

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers };
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  const loadReport = async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`/api/campaigns/${campaignId}/report`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Falha ao carregar relatório');
      }
      const data = await res.json();
      setReportData(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar relatório');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [campaignId]);

  const handleDownload = async () => {
    if (!campaignId || !reportData) return;
    try {
      const res = await authenticatedFetch(`/api/campaigns/${campaignId}/report/download`);
      if (!res.ok) throw new Error('Falha ao baixar');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-campanha-${reportData.campaign?.nome || 'campanha'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Relatório baixado com sucesso!');
    } catch {
      toast.error('Erro ao baixar relatório');
    }
  };

  if (!campaignId) {
    return (
      <>
        <Header title="Relatório" compactTopbar />
        <PageLayout>
          <div className="card-padrao p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">Campanha não informada.</p>
            <button type="button" onClick={() => navigate('/campanhas/relatorios')} className="mt-4 btn-atualizar">Voltar aos Relatórios</button>
          </div>
        </PageLayout>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header title="Relatório" compactTopbar />
        <PageLayout>
          <PageHeader title="Relatório" breadcrumbs={[{ label: 'Campanhas', href: '/campanhas' }, { label: 'Relatórios', href: '/campanhas/relatorios' }, { label: 'Relatório' }]} />
          <div className="card-padrao p-8 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button type="button" onClick={() => navigate('/campanhas/relatorios')} className="mt-4 btn-atualizar">Voltar aos Relatórios</button>
          </div>
        </PageLayout>
      </>
    );
  }

  const campaign = reportData?.campaign;
  const stats = reportData?.stats;
  const messagesBySession = reportData?.messagesBySession || {};
  const messages = campaign?.messages || [];
  const totalReportPages = Math.max(1, Math.ceil(messages.length / REPORT_ITEMS_PER_PAGE));
  const startIdx = (reportPage - 1) * REPORT_ITEMS_PER_PAGE;
  const currentMessages = messages.slice(startIdx, startIdx + REPORT_ITEMS_PER_PAGE);

  return (
    <>
      <Header title={campaign?.nome ? `Relatório: ${campaign.nome}` : 'Relatório'} compactTopbar />
      <PageLayout>
        <PageHeader
          title={campaign?.nome ? `Relatório: ${campaign.nome}` : 'Relatório da campanha'}
          breadcrumbs={[
            { label: 'Campanhas', href: '/campanhas' },
            { label: 'Relatórios', href: '/campanhas/relatorios' },
            { label: campaign?.nome || 'Relatório' },
          ]}
        />

        {loading ? (
          <div className="card-padrao flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-600 border-t-emerald-500" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Carregando relatório...</span>
          </div>
        ) : reportData ? (
          <div className="space-y-6">
            {/* Info da campanha */}
            <div className="card-padrao p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-gray-800 dark:text-white/90">Informações da campanha</h3>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <button
                    type="button"
                    onClick={loadReport}
                    disabled={loading}
                    className="btn-atualizar"
                  >
                    <i
                      className={`fas fa-arrows-rotate text-base shrink-0 ${loading ? 'animate-spin' : ''}`}
                      aria-hidden
                    />
                    {loading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/campanhas/relatorios')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                    Voltar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${getStatusColor(campaign.status)}`}>
                    {getStatusText(campaign.status)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Criado por:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">{campaign.createdByName || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Data de criação:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">{new Date(campaign.criadoEm).toLocaleString('pt-BR')}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Data de início:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">{campaign.startedAt ? new Date(campaign.startedAt).toLocaleString('pt-BR') : 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Data de conclusão:</span>
                  <span className="ml-2 text-gray-900 dark:text-white/90">{campaign.completedAt ? new Date(campaign.completedAt).toLocaleString('pt-BR') : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="card-padrao p-6">
              <h3 className="font-semibold text-gray-800 dark:text-white/90 mb-4">Estatísticas de envio</h3>
            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-address-book" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Total de contatos</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.total ?? 0}</h3>
              </div>
              <div className="border-b p-5 lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 text-xl" aria-hidden>
                    <i className="fas fa-circle-check" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Enviadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.sent ?? 0}</h3>
              </div>
              <div className="border-b p-5 sm:border-r sm:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                    <i className="fas fa-times-circle" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Falharam</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.failed ?? 0}</h3>
              </div>
              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                    <i className="fas fa-clock" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Pendentes</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{stats?.pending ?? 0}</h3>
              </div>
            </div>
            </div>

            {/* Por sessão */}
            {Object.keys(messagesBySession).length > 0 && (
              <div className="card-padrao overflow-hidden">
                <h3 className="font-semibold text-gray-800 dark:text-white/90 px-6 py-4 border-b border-gray-200 dark:border-gray-700">Distribuição por sessão</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sessão</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Conectado como</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Enviadas</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Falharam</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(messagesBySession).map(([sessionName, sessionData]: [string, any]) => {
                        const msgs = sessionData?.messages || [];
                        return (
                          <tr key={sessionName}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{sessionName}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                              {sessionData?.mePushName ? (
                                <span>{sessionData.mePushName}{sessionData.meId ? <span className="text-gray-400 dark:text-gray-500"> ({sessionData.meId})</span> : ''}</span>
                              ) : (
                                '–'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{msgs.length}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400">{msgs.filter((m: any) => m.status === 'SENT').length}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">{msgs.filter((m: any) => m.status === 'FAILED').length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detalhes das mensagens */}
            <div className="card-padrao overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
                <h3 className="font-semibold text-gray-800 dark:text-white/90">Detalhes das mensagens</h3>
                <button type="button" onClick={handleDownload} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700">
                  <i className="fas fa-download text-base shrink-0" aria-hidden />
                  Download CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Telefone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sessão</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data de envio</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Erro</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                    {currentMessages.map((message: any) => (
                      <tr key={message.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white/90">{message.contactName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white/90">{message.contactPhone}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${
                            message.status === 'SENT' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' :
                            message.status === 'FAILED' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                            'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                          }`}>
                            {message.status === 'SENT' ? 'Enviada' : message.status === 'FAILED' ? 'Falhou' : 'Pendente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{message.sessionName || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {message.errorMessage && message.errorMessage !== 'N/A' ? (() => {
                            const errorInfo = getErrorIcon(message.errorMessage);
                            const formatted = formatQuotaErrorForDisplay(message.errorMessage);
                            return errorInfo ? (
                              <span
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${errorInfo.bgColor} cursor-help text-lg`}
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setErrorTooltip({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 8,
                                    formatted,
                                    category: errorInfo.category,
                                    icon: errorInfo.icon,
                                    bgColor: errorInfo.bgColor,
                                    color: errorInfo.color,
                                  });
                                }}
                                onMouseLeave={scheduleHideErrorTooltip}
                              >
                                {errorInfo.icon}
                              </span>
                            ) : (
                              <span
                                className="text-sm text-red-600 dark:text-red-400 whitespace-pre-line max-w-xs block"
                              >
                                {formatted}
                              </span>
                            );
                          })() : (
                            <span className="text-gray-400 dark:text-gray-500">–</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {messages.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhuma mensagem encontrada</div>
              )}
              {totalReportPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Mostrando {startIdx + 1}–{Math.min(startIdx + REPORT_ITEMS_PER_PAGE, messages.length)} de {messages.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setReportPage((p) => Math.max(1, p - 1))} disabled={reportPage === 1} className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">Anterior</button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Página {reportPage} de {totalReportPages}</span>
                    <button type="button" onClick={() => setReportPage((p) => Math.min(totalReportPages, p + 1))} disabled={reportPage === totalReportPages} className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">Próximo</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </PageLayout>
      {errorTooltip &&
        createPortal(
          <div
            className="fixed z-[9999] max-w-xs rounded-xl bg-gray-900 px-3 py-2 text-xs text-gray-100 shadow-xl ring-1 ring-black/40"
            style={{
              left: errorTooltip.x,
              top: errorTooltip.y,
              transform: 'translate(-50%, -100%)',
            }}
            onMouseEnter={cancelHideErrorTooltip}
            onMouseLeave={scheduleHideErrorTooltip}
          >
            <div className="mb-1 flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${errorTooltip.bgColor} ${errorTooltip.color}`}>
                {errorTooltip.icon} {errorTooltip.category}
              </span>
            </div>
            <p className="text-[11px] leading-relaxed whitespace-pre-line text-gray-100">
              {errorTooltip.formatted}
            </p>
          </div>,
          document.body
        )}
    </>
  );
}
